import { Injectable } from '@angular/core';
import { GlobalConstants } from '../constant/constant';
import { Activity } from '../entities/Activity';
import { ActivityLog } from '../entities/ActivityLog';
import { Attachment } from '../entities/Attachment';
import { EntityType } from '../entities/EntityType';
import { FieldActivity } from '../entities/FieldActivity';
import { FIPSState } from '../entities/FIPSState';
import { LaborCategory } from '../entities/LaborCategory';
import { NotableActivityOption } from '../entities/NotableActivityOption';
import { OdometerReading } from '../entities/OdometerReading';
import { Project } from '../entities/Project';
import { StatusLookup } from '../entities/StatusLookup';
import { StoreName } from '../entities/StoreName';
import { FlattendSyncParams, SyncParams, SyncDownloadType } from '../entities/SyncParams';
import { Task } from '../entities/Task';
import { Team } from '../entities/Team';
import { TeamUser } from '../entities/TeamUser';
import { TimeManagerProject } from '../entities/TimeManagerProject';
import { TimeManagerTeam } from '../entities/TimeManagerTeam';
import { TimeRecord } from '../entities/timeRecord';
import { TimeRecordStatus } from '../entities/TimeRecordStatus';
import { User } from '../entities/User';
import { UserFieldActivity } from '../entities/UserFieldActivity';
import { UserProject } from '../entities/UserProject';
import { AppEventType } from '../enums/app-events.enum';
import { DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import { SyncResponse, SyncResponseStoreNamesMap } from '../models/sync-download/sync-response-interface';
import { ActivityService } from '../services/activity.service';
import { ActivityLogService } from '../services/activityLog.service';
import { AttachmentService } from '../services/attachment.service';
import { DataService } from '../services/data.service';
import { EntityTypeService } from '../services/entity-type.service';
import { EventBusService } from '../services/event-bus.service';
import { FieldActivityService } from '../services/field-activity.service';
import { FIPSStateService } from '../services/fips-state.service';
import { LaborCategoryService } from '../services/labor-category.service';
import { NotableActivityOptionService } from '../services/notable-activity-option.service';
import { NotificationsService } from '../services/notifications.service';
import { OdometerReadingService } from '../services/odometer-reading.service';
import { ProjectService } from '../services/project.service';
import { StatusLookupService } from '../services/status-lookup.service';
import { TaskService } from '../services/task.service';
import { TeamUserService } from '../services/team-user.service';
import { TeamService } from '../services/team.service';
import { TimeManagerProjectService } from '../services/time-manager-project.service';
import { TimeManagerTeamService } from '../services/time-manager-team.service';
import { TimeRecordStatusService } from '../services/time-record-status.service';
import { TimeRecordService } from '../services/timeRecord.service';
import { UserFieldActivityService } from '../services/user-field-activity.service';
import { UserProjectService } from '../services/user-project.service';
import { UserService } from '../services/user.service';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { AggregateException, ValidationException } from '../utils/exceptions';
import { LogService } from './logging/log.service';
import { OdometerReadingMedia } from '../entities/OdometerReadingMedia';

const SYNC_THROTTLE_INTERVAL_MINUTES = {
  [SyncDownloadType.AppUser]: 1,
  [SyncDownloadType.Team]: 3,
  [SyncDownloadType.Project]: 3,
} as const;

type StoreNameEntityMap = {
  [StoreName.Activity]: Activity;
  [StoreName.ActivityLog]: ActivityLog;
  [StoreName.TimeRecord]: TimeRecord;
  [StoreName.Project]: Project;
  [StoreName.User]: User;
  [StoreName.StatusLookup]: StatusLookup;
  [StoreName.EntityType]: EntityType;
  [StoreName.FIPSState]: FIPSState;
  [StoreName.Attachment]: Attachment;
  [StoreName.LaborCategory]: LaborCategory;
  [StoreName.NotableActivityOption]: NotableActivityOption;
  [StoreName.Task]: Task;
  [StoreName.TimeRecordStatus]: TimeRecordStatus;
  [StoreName.Team]: Team;
  [StoreName.Team2User]: TeamUser;
  [StoreName.TimeManager2Project]: TimeManagerProject;
  [StoreName.TimeManager2Team]: TimeManagerTeam;
  [StoreName.FieldActivity]: FieldActivity;
  [StoreName.Users2FieldActivity]: UserFieldActivity;
  [StoreName.Users2Project]: UserProject;
  [StoreName.OdometerReading]: OdometerReading;
  [StoreName.OdometerReadingMedia]: OdometerReadingMedia;
};

const APP_USER_KEY_ID = 'app-user';

@Injectable({
  providedIn: 'root',
})
export class SyncDownloadService extends DataService {
  private readonly SERVICE_HANDLERS_MAP = new Map<
    keyof StoreNameEntityMap,
    (who: DataChangeEmitWho, record: StoreNameEntityMap[keyof StoreNameEntityMap], existingTransaction?: DbTransaction) => Promise<void>
  >([
    [StoreName.Activity, (who, record: Activity, transaction) => this.activityService.upsertActivityFromSync(who, record, transaction)],
    [StoreName.ActivityLog, (who, record: ActivityLog, transaction) => this.activityLogService.upsertActivityLogFromSync(who, record, transaction)],
    [StoreName.TimeRecord, (who, record: TimeRecord, transaction) => this.timeRecordService.upsertTimeRecordFromSync(who, record, transaction)],
    [StoreName.Project, (who, record: Project, transaction) => this.projectService.upsertProjectFromSync(who, record, transaction)],
    [StoreName.User, (who, record: User, transaction) => this.userService.upsertUserFromSync(who, record, transaction)],
    [StoreName.StatusLookup, (who, record: StatusLookup, transaction) => this.statusLookupService.upsertStatusLookupFromSync(who, record, transaction)],
    [StoreName.EntityType, (who, record: EntityType, transaction) => this.entityTypeService.upsertEntityTypeFromSync(who, record, transaction)],
    [StoreName.FIPSState, (who, record: FIPSState, transaction) => this.fipsStateService.upsertFIPSStateFromSync(who, record, transaction)],
    [StoreName.TimeRecordStatus, (who, record: TimeRecordStatus, transaction) => this.timeRecordStatusService.upsertTimeRecordStatusFromSync(who, record, transaction)],
    [StoreName.Attachment, (who, record: Attachment, transaction) => this.attachmentService.upsertAttachmentFromSync(who, record, transaction)],
    [StoreName.LaborCategory, (who, record: LaborCategory, transaction) => this.laborCategoryService.upsertLaborCategoryFromSync(who, record, transaction)],
    [StoreName.NotableActivityOption, (who, record: NotableActivityOption, transaction) => this.notableActivityOptionService.upsertNotableActivityOptionFromSync(who, record, transaction)],
    [StoreName.Task, (who, record: Task, transaction) => this.taskService.upsertTaskFromSync(who, record, transaction)],
    [StoreName.Team, (who, record: Team, transaction) => this.teamService.upsertTeamFromSync(who, record, transaction)],
    [StoreName.Team2User, (who, record: TeamUser, transaction) => this.teamUserService.upsertTeamUserFromSync(who, record, transaction)],
    [StoreName.TimeManager2Project, (who, record: TimeManagerProject, transaction) => this.timeManagerProjectService.upsertTimeManagerProjectFromSync(who, record, transaction)],
    [StoreName.TimeManager2Team, (who, record: TimeManagerTeam, transaction) => this.timeManagerTeamService.upsertTimeManagerTeamFromSync(who, record, transaction)],
    [StoreName.FieldActivity, (who, record: FieldActivity, transaction) => this.fieldActivityService.upsertFieldActivityFromSync(who, record, transaction)],
    [StoreName.Users2FieldActivity, (who, record: UserFieldActivity, transaction) => this.userFieldActivityService.upsertUserFieldActivityFromSync(who, record, transaction)],
    [StoreName.Users2Project, (who, record: UserProject, transaction) => this.userProjectService.upsertUserProjectFromSync(who, record, transaction)],
    [StoreName.OdometerReading, (who, record: OdometerReading, transaction) => this.odometerReadingService.upsertOdometerReadingFromSync(who, record, transaction)],
  ]);
  private shouldResyncUserBadge: boolean = false;
  
  /**
   * Tracks the last sync time to prevent redundant sync operations.
   * Key format: 'entity-type:entity-id' (e.g., 'team:123', 'project:456', 'app-user')
   */
  private lastSyncTimes = new Map<string, Date>();

  constructor(
    private notificationService: NotificationsService,
    private timeRecordService: TimeRecordService,
    private activityService: ActivityService,
    private projectService: ProjectService,
    private userService: UserService,
    private activityLogService: ActivityLogService,
    private statusLookupService: StatusLookupService,
    private entityTypeService: EntityTypeService,
    private fipsStateService: FIPSStateService,
    private attachmentService: AttachmentService,
    private laborCategoryService: LaborCategoryService,
    private notableActivityOptionService: NotableActivityOptionService,
    private taskService: TaskService,
    private timeRecordStatusService: TimeRecordStatusService,
    private teamService: TeamService,
    private teamUserService: TeamUserService,
    private timeManagerProjectService: TimeManagerProjectService,
    private timeManagerTeamService: TimeManagerTeamService,
    private fieldActivityService: FieldActivityService,
    private userFieldActivityService: UserFieldActivityService,
    private userProjectService: UserProjectService,
    private odometerReadingService: OdometerReadingService,
    private eventBusService: EventBusService,
    private logService: LogService
  ) {
    super();
  }

  /**
   * Creates a sync key for throttling purposes.
   * @param syncType The type of sync ('app-user', 'team', 'project')
   * @param entityId The entity ID (or undefined for app-user)
   * @returns Composite key like 'team:123' or 'app-user'
   */
  private createSyncKey(syncType: SyncDownloadType, entityId?: string): string {
    return entityId ? `${syncType}:${entityId}` : syncType;
  }

  /**
   * Checks if a particular sync is throttled based on the last sync time.
   * @param syncType The type of sync to check
   * @param entityId The entity ID (optional for app-user)
   * @returns true if sync should be throttled, false if sync is allowed
   */
  private checkIsSyncThrottled(syncType: SyncDownloadType, entityId?: string): boolean {
    const syncKey = this.createSyncKey(syncType, entityId);
    const lastSyncTime = this.lastSyncTimes.get(syncKey);
    
    if (!lastSyncTime) {
      return false; // Never synced, allow sync
    }

    const throttleMinutes = SYNC_THROTTLE_INTERVAL_MINUTES[syncType];
    const now = new Date();
    const timeSinceLastSync = now.getTime() - lastSyncTime.getTime();
    const throttleMs = throttleMinutes * 60 * 1000;

    return timeSinceLastSync < throttleMs;
  }

  /**
   * Records that a sync was performed at the current time.
   * @param syncType The type of sync
   * @param entityId The entity ID (optional for app-user)
   */
  private setLastSyncTime(syncType: SyncDownloadType, entityId?: string): void {
    const syncKey = this.createSyncKey(syncType, entityId);
    this.lastSyncTimes.set(syncKey, new Date());
  }

  private async getAllSyncParams(): Promise<SyncParams[]> {
    return this.repository.getAll<SyncParams>(StoreName.SyncParams);
  }

  private async getSyncParams(type: SyncDownloadType, id: string, existingTransaction?: DbTransaction): Promise<SyncParams> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const key: DbKeyType = {
      lower: [type, id],
      upper: [type, id],
    };
    const result = await this.repository.getOneByIndex<SyncParams>(StoreName.SyncParams, GlobalConstants.KEY_NAMES.SyncType_Id, key, undefined, transaction);

    return result;
  }

  private async getFlattenedSyncParams(type: SyncDownloadType, id: string, startDate: DateTimeType, endDate: DateTimeType, existingTransaction?: DbTransaction): Promise<FlattendSyncParams> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const syncParams = await this.getSyncParams(type, id, transaction);

    if (syncParams) {
      let timeDataFilter = syncParams.TimeDataFilters.find(filter => DateTimeHelper.checkOnSameDay(filter.StartDate, startDate) && DateTimeHelper.checkOnSameDay(filter.EndDate, endDate));
      return {
        Type: type,
        Id: id,
        ReferenceDataModifiedDateFrom: this.bufferModifiedDateFrom(syncParams.ReferenceDataModifiedDateFrom),
        TimeDataStartDate: DateTimeHelper.formatDateTimeAsDate(startDate),
        TimeDataEndDate: DateTimeHelper.formatDateTimeAsDate(endDate),
        TimeDataModifiedDateFrom: this.bufferModifiedDateFrom(timeDataFilter?.ModifiedDateFrom),
      };
    }
    else {
      return {
        Type: type,
        Id: id,
        ReferenceDataModifiedDateFrom: null,
        TimeDataStartDate: DateTimeHelper.formatDateTimeAsDate(startDate),
        TimeDataEndDate: DateTimeHelper.formatDateTimeAsDate(endDate),
        TimeDataModifiedDateFrom: null,
      };
    }
  }

  /**
   * Subtracts 1 minute from the provided modifiedDateFrom to create a buffer for sync operations.
   * This helps prevent missing records that were modified right at the edge of the last sync time.
   */
  private bufferModifiedDateFrom(modifiedDateFrom: string | null): string | null {
    return modifiedDateFrom
      ? DateTimeHelper.formatDateTime(DateTimeHelper.subtractTime(modifiedDateFrom, GlobalConstants.SYNC_MODIFIED_DATE_BUFFER_MINUTES, "minute"))
      : null;
  }

  private buildSyncUrl(flattendSyncParams: FlattendSyncParams, isFullSync: boolean): string {
    const params = {
      ReferenceDataModifiedDateFrom: isFullSync ? null : flattendSyncParams.ReferenceDataModifiedDateFrom,
      TimeDataStartDate: flattendSyncParams.TimeDataStartDate,
      TimeDataEndDate: flattendSyncParams.TimeDataEndDate,
      TimeDataModifiedDateFrom: isFullSync ? null : flattendSyncParams.TimeDataModifiedDateFrom,
    };

    switch (flattendSyncParams.Type) {
      case SyncDownloadType.AppUser:
        return this.http.buildUrl(this.MOBILE_API_ENDPOINTS.SyncAppUser, params);
      case SyncDownloadType.Team:
        return this.http.buildUrl(this.MOBILE_API_ENDPOINTS.SyncTeam, { teamId: flattendSyncParams.Id, ...params });
      case SyncDownloadType.Project:
        return this.http.buildUrl(this.MOBILE_API_ENDPOINTS.SyncProject, { projectId: flattendSyncParams.Id, ...params });
      default:
        throw new Error('Invalid sync type');
    }
  }

  async updateSyncParams(lastSyncDate: DateTimeType, type: SyncDownloadType, id: string, startDate: DateTimeType, endDate: DateTimeType, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingSyncParams = await this.getSyncParams(type, id, transaction);

    if (existingSyncParams) {
      const updatedSyncParams = existingSyncParams;
      updatedSyncParams.ReferenceDataModifiedDateFrom = DateTimeHelper.formatDateTime(lastSyncDate);

      const timeDataFilterIndex = updatedSyncParams.TimeDataFilters.findIndex(filter => DateTimeHelper.checkOnSameDay(filter.StartDate, startDate) && DateTimeHelper.checkOnSameDay(filter.EndDate, endDate));
      if (timeDataFilterIndex !== -1) {
        const timeDataFilter = updatedSyncParams.TimeDataFilters[timeDataFilterIndex];
        timeDataFilter.ModifiedDateFrom = DateTimeHelper.formatDateTime(lastSyncDate);
        updatedSyncParams.TimeDataFilters[timeDataFilterIndex] = timeDataFilter;
      }
      else {
        updatedSyncParams.TimeDataFilters.push({
          StartDate: DateTimeHelper.formatDateTimeAsDate(startDate),
          EndDate: DateTimeHelper.formatDateTimeAsDate(endDate),
          ModifiedDateFrom: DateTimeHelper.formatDateTime(lastSyncDate),
        });
      }

      await this.repository.update(updatedSyncParams, StoreName.SyncParams, transaction);
    }
    else {
      const newSyncParams = {
        Type: type,
        Id: id,
        ReferenceDataModifiedDateFrom: DateTimeHelper.formatDateTime(lastSyncDate),
        TimeDataFilters: [{
          StartDate: DateTimeHelper.formatDateTimeAsDate(startDate),
          EndDate: DateTimeHelper.formatDateTimeAsDate(endDate),
          ModifiedDateFrom: DateTimeHelper.formatDateTime(lastSyncDate),
        }],
      };

      await this.repository.create(newSyncParams, StoreName.SyncParams, transaction);
    }
  }

  async getAppUserLastSyncDate(): Promise<string> {
    const appUserSyncParams = await this.getSyncParams(SyncDownloadType.AppUser, APP_USER_KEY_ID);
    return appUserSyncParams?.ReferenceDataModifiedDateFrom ?? null;
  }

  private async sync(type: SyncDownloadType, id: string, startDate: DateTimeType, endDate: DateTimeType, isFullSync: boolean) {
    if (isFullSync) {
      this.logService.info('SyncDownload.FullSyncStarted', { syncType: type, entityId: id, startDate, endDate }, true);
    }
    
    const syncStartTime = DateTimeHelper.getUtcNow();
    const syncParams = await this.getFlattenedSyncParams(type, id, startDate, endDate);
    const syncUrl = this.buildSyncUrl(syncParams, isFullSync);

    const response = await this.http.get<SyncResponse>(syncUrl).toPromise();

    const transaction = await this.startTransactionIfNeeded();
    const promises: Promise<void | number>[] = [];

    try {
      for (const key in response.body) {
        const recordsByEntity: any[] = response.body[key];
        const storeName: StoreName = SyncResponseStoreNamesMap[key];

        if (storeName === StoreName.User) {
          const currentUserId = this.userService.getCurrentUserId();
          this.shouldResyncUserBadge = recordsByEntity.some((record: User) => record.Id === currentUserId);
        }

        if (recordsByEntity.length > 0) {
          const handler = this.SERVICE_HANDLERS_MAP.get(storeName as keyof StoreNameEntityMap);
          if (handler) {
            recordsByEntity.forEach(record => promises.push(handler(DataChangeEmitWho.Sync, record, transaction)));
          }
        }
      }

      promises.push(this.updateSyncParams(syncStartTime, type, id, startDate, endDate, transaction));

      await Promise.all(promises);

      await transaction.commit().catch(async e => {
        if (e instanceof AggregateException) {
          const areAllValidationErrors = e.errors.every(error => error instanceof ValidationException);
          if (areAllValidationErrors) {
            await this.handleValidationException(e.errors[0]);
          } else {
            throw e.errors[0];
          }
        } else {
          throw e;
        }
      });
    } catch (error) {
      transaction.rollback();
      this.logService.error('SyncDownload.SyncError', error, { syncType: type, entityId: id, isFullSync, startDate, endDate });
    }
  }

  private async handleValidationException(e: ValidationException): Promise<void> {

    await this.notificationService.showAlert({
      header: 'Sync Errors',
      message: `<p>Invalid or conflicting data was received. This may cause unexpected behavior.</p>
          <p>Error Details: ${e.message}</p>
          <p>If issues persist, please contact support.</p>`,
      buttons: [
        {
          text: 'OK',
        },
      ],
      suppression: {
        suppressKey: GlobalConstants.SUPPRESS_INVALID_SYNC_ALERT_DATE,
        suppressDuration: 24,
        suppressLabel: 'Hide this message',
      },
    });

    this.logService.warn('SyncDownload.ValidationError', { error: e }, true);
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    const storeNames = [...Object.values(StoreName)];
    return existingTransaction || (await this.repository.startTransaction(storeNames, 'readwrite'));
  }

  async syncAppUser(startDate: DateTimeType, endDate: DateTimeType, fullSync = false, ignoreThrottle = false) {
    if (!ignoreThrottle && this.checkIsSyncThrottled(SyncDownloadType.AppUser)) {
      return;
    }

    await this.sync(SyncDownloadType.AppUser, APP_USER_KEY_ID, startDate, endDate, fullSync);
    this.setLastSyncTime(SyncDownloadType.AppUser);
  }

  async syncTeam(teamId: number, startDate: DateTimeType, endDate: DateTimeType, fullSync = false, ignoreThrottle = false) {
    const teamIdStr = teamId.toString();
    
    if (!ignoreThrottle && this.checkIsSyncThrottled(SyncDownloadType.Team, teamIdStr)) {
      return;
    }

    await this.sync(SyncDownloadType.Team, teamIdStr, startDate, endDate, fullSync);
    this.setLastSyncTime(SyncDownloadType.Team, teamIdStr);

    this.eventBusService.emit({ type: AppEventType.SYNCED_TEAM_DATA, data: { teamId } });
  }

  async syncProject(projectId: number, startDate: DateTimeType, endDate: DateTimeType, fullSync = false, ignoreThrottle = false) {
    const projectIdStr = projectId.toString();
    
    if (!ignoreThrottle && this.checkIsSyncThrottled(SyncDownloadType.Project, projectIdStr)) {
      return;
    }

    await this.sync(SyncDownloadType.Project, projectIdStr, startDate, endDate, fullSync);
    this.setLastSyncTime(SyncDownloadType.Project, projectIdStr);
    
    this.eventBusService.emit({ type: AppEventType.SYNCED_PROJECT_DATA, data: { projectId } });
  }

  async syncAll(startDate: DateTimeType, endDate: DateTimeType, fullSync = false, ignoreThrottle = false) {
    await this.syncAppUser(startDate, endDate, fullSync, ignoreThrottle);

    const promises: Promise<void>[] = [];

    const teamIdsToSync = await this.teamService.getManagedTeamIds();
    promises.push(...teamIdsToSync.map(teamId => this.syncTeam(teamId, startDate, endDate, fullSync, ignoreThrottle)));

    const projectIdsToSync = await this.teamService.getManagedProjectIds();
    promises.push(...projectIdsToSync.map(projectId => this.syncProject(projectId, startDate, endDate, fullSync, ignoreThrottle)));

    await Promise.all(promises);
  }
}
